import { toSvg } from 'html-to-image';
import { Children, isValidElement, type ReactNode } from 'react';

/** Marks the element an ExportMenu captures; the menu exports its closest such ancestor. */
export const EXPORT_ROOT_ATTR = 'data-export-root';
/** Elements with this attribute (the export buttons themselves) are left out of the PNG. */
export const EXPORT_IGNORE_ATTR = 'data-export-ignore';

export type CsvRow = Record<string, unknown>;

/** A safe file name from a card title, e.g. "Daily spend by tool" → "daily-spend-by-tool-2026-10-26". */
export function exportFileName(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
  return `${slug}-${new Date().toISOString().slice(0, 10)}`;
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  // Leading = + - @ would run as a formula when the file is opened in a spreadsheet.
  const safe = /^[=+\-@\t\r]/.test(text) && Number.isNaN(Number(text)) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Flattens one level of nesting (e.g. `byType.input`) and drops non-primitive values. */
export function flattenRow(row: CsvRow): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(row)) {
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) out[key] = value as string | number | boolean | null;
    else if (typeof value === 'object' && !Array.isArray(value)) {
      for (const [inner, innerValue] of Object.entries(value as CsvRow)) {
        if (innerValue === null || ['string', 'number', 'boolean'].includes(typeof innerValue)) {
          out[`${key}.${inner}`] = innerValue as string | number | boolean | null;
        }
      }
    }
  }
  return out;
}

export function toCsv(rows: CsvRow[]): string {
  const flat = rows.map(flattenRow);
  const columns = [...new Set(flat.flatMap((r) => Object.keys(r)))];
  return [columns.map(csvCell).join(','), ...flat.map((r) => columns.map((c) => csvCell(r[c])).join(','))].join('\r\n');
}

/** Reads a rendered table as rows keyed by its header text, so a CSV matches what is on screen. */
export function tableToRows(table: HTMLTableElement): CsvRow[] {
  const headerCells = table.tHead?.rows[0]?.cells ?? [];
  const headers = [...headerCells].map((th, i) => th.textContent?.trim() || `Column ${i + 1}`);
  // Direct rows only: expanded detail rows hold nested tables and span the columns.
  return [...table.tBodies]
    .flatMap((body) => [...body.rows])
    .map((tr) => [...tr.cells])
    .filter((cells) => cells.length === headers.length)
    .map((cells) => Object.fromEntries(cells.map((td, i) => [headers[i], td.textContent?.trim() ?? ''])));
}

function download(href: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.click();
}

export function downloadCsv(rows: CsvRow[], name: string): void {
  // The byte-order mark makes Excel open the file as UTF-8.
  const url = URL.createObjectURL(new Blob(['\uFEFF', toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  download(url, `${name}.csv`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadPng(element: HTMLElement, name: string): Promise<void> {
  const background = getComputedStyle(document.documentElement).getPropertyValue('--surface').trim() || '#FFFFFF';
  const svg = await toSvg(element, {
    // Embedding web fonts means re-fetching cross-origin stylesheets (Font Awesome), which is slow and blocked
    // by CORS; the image falls back to system fonts instead.
    skipFonts: true,
    filter: (node: HTMLElement) => !(node instanceof Element && node.hasAttribute(EXPORT_IGNORE_ATTR)),
  });
  // Rasterised here rather than with toPng, which waits on requestAnimationFrame and stalls in a hidden tab.
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Could not render the image'));
    image.src = svg;
  });
  const scale = 2;
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is not available');
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  download(canvas.toDataURL('image/png'), `${name}.png`);
}

const IGNORED_PROPS = new Set(['children', 'style', 'className', 'contentStyle', 'labelStyle', 'itemStyle', 'wrapperStyle', 'cursor', 'tick', 'margin', 'label']);

function isPlainObject(value: unknown): value is CsvRow {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !isValidElement(value) && Object.getPrototypeOf(value) === Object.prototype;
}

function isPrimitiveRecord(value: unknown): value is CsvRow {
  return isPlainObject(value) && Object.values(value).some((v) => typeof v === 'number') &&
    Object.values(value).every((v) => v === null || ['string', 'number', 'boolean'].includes(typeof v));
}

/**
 * Finds the data a chart card renders, so its CSV needs no extra wiring: the
 * first `data` array (a Recharts chart), else the first array of records or
 * record of numbers passed to a chart component, in render order.
 */
export function findChartData(children: ReactNode, depth = 0): CsvRow[] | null {
  if (depth > 8) return null;
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    const props = child.props as Record<string, unknown>;
    if (Array.isArray(props.data) && props.data.every(isPlainObject)) return props.data as CsvRow[];
    for (const [key, value] of Object.entries(props)) {
      if (IGNORED_PROPS.has(key)) continue;
      if (Array.isArray(value) && value.length > 0 && value.every(isPlainObject)) return value as CsvRow[];
      if (isPrimitiveRecord(value)) return [value];
    }
    const nested = findChartData(props.children as ReactNode, depth + 1);
    if (nested) return nested;
  }
  return null;
}
